<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot, useId } from 'reka-ui';
import { computed } from 'vue';

const model = defineModel<boolean>({ default: false });

interface Props {
    label?: string;
    ariaLabel?: string;
    ariaDescribedby?: string;
    name?: string;
    id?: string;
    disabled?: boolean;
    value?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
});

const emit = defineEmits<{
    (e: 'change', value: boolean): void;
}>();

const generatedId = useId();
const checkboxId = computed(() => props.id || generatedId);

const hasLabel = computed(() => !!props.label);

/**
 * Updates the model and notifies the parent through the `change` event.
 */
const setChecked = (checked: boolean): void => {
    if (props.disabled) {
        return;
    }

    model.value = checked;
    emit('change', checked);
};
</script>

<template>
    <div class="flex items-center gap-2.5">
        <CheckboxRoot
            :id="checkboxId"
            :model-value="model"
            :disabled="disabled"
            :value="value"
            :aria-label="ariaLabel || label || undefined"
            :aria-describedby="ariaDescribedby"
            :name="name"
            :class="[
                'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border transition-colors',
                'focus-visible:ring-terracotta-soft focus-visible:ring-offset-surface focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                model
                    ? 'border-terracotta bg-terracotta text-on-terracotta'
                    : 'border-muted bg-surface hover:border-terracotta',
            ]"
            @update:model-value="setChecked($event === true)"
        >
            <CheckboxIndicator>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-3.5 w-3.5"
                >
                    <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                    />
                </svg>
            </CheckboxIndicator>
        </CheckboxRoot>

        <label
            v-if="hasLabel"
            :for="checkboxId"
            class="text-ink cursor-pointer text-sm select-none"
            @click.prevent="setChecked(!model)"
        >
            {{ label }}
        </label>
    </div>
</template>
